'use client';

import { useEffect } from 'react';

import Script from 'next/script';

/**
 * Regions where Google Consent Mode v2 must default to `denied`.
 * Covers the EEA (EU + EEA members) + UK + Switzerland per 2024/2025 guidance.
 * Consent Mode v2 has been REQUIRED for personalized advertising to
 * EEA traffic since March 2024; without it Google blocks EEA audiences.
 *
 * The actual region of the user is detected by Google based on IP at
 * the time the tag fires; we simply declare the region-filter so the
 * consent defaults apply there without gating anything for non-EEA.
 */
const CONSENT_REGIONS = [
  'AT',
  'BE',
  'BG',
  'CH',
  'CY',
  'CZ',
  'DE',
  'DK',
  'EE',
  'ES',
  'FI',
  'FR',
  'GB',
  'GR',
  'HR',
  'HU',
  'IE',
  'IS',
  'IT',
  'LI',
  'LT',
  'LU',
  'LV',
  'MT',
  'NL',
  'NO',
  'PL',
  'PT',
  'RO',
  'SE',
  'SI',
  'SK',
];

/**
 * Wires Google Consent Mode v2:
 *   - Sets `default` consent to `denied` for EEA/UK/CH users BEFORE any
 *     Google/Meta tag fires, so cookieless pings run and modeled
 *     conversions are recoverable.
 *   - Listens to the `cookie-consent-update` event dispatched by
 *     CookieConsentProvider and pushes a `consent update` to gtag,
 *     unlocking full measurement when the user accepts.
 *
 * Safe to render on every marketing page — bails out cleanly if gtag
 * is absent.
 */
export function ConsentMode() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handler = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          analytics: boolean;
          marketing: boolean;
        }>
      ).detail;
      if (!detail) return;

      const gtag = (
        window as unknown as { gtag?: (...args: unknown[]) => void }
      ).gtag;
      if (typeof gtag !== 'function') return;

      gtag('consent', 'update', {
        ad_storage: detail.marketing ? 'granted' : 'denied',
        ad_user_data: detail.marketing ? 'granted' : 'denied',
        ad_personalization: detail.marketing ? 'granted' : 'denied',
        analytics_storage: detail.analytics ? 'granted' : 'denied',
      });
    };

    window.addEventListener('cookie-consent-update', handler);
    return () => window.removeEventListener('cookie-consent-update', handler);
  }, []);

  return (
    <Script
      id="gtag-consent-default"
      strategy="beforeInteractive"
      dangerouslySetInnerHTML={{
        __html: `
          window.dataLayer = window.dataLayer || [];
          function gtag(){window.dataLayer.push(arguments);}
          window.gtag = window.gtag || gtag;
          // Consent Mode v2 defaults. EEA/UK/CH: deny until banner accept.
          gtag('consent', 'default', {
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            analytics_storage: 'denied',
            wait_for_update: 500,
            region: ${JSON.stringify(CONSENT_REGIONS)}
          });
          // Outside those regions: grant by default so tags fire normally.
          gtag('consent', 'default', {
            ad_storage: 'granted',
            ad_user_data: 'granted',
            ad_personalization: 'granted',
            analytics_storage: 'granted'
          });
        `,
      }}
    />
  );
}
